import React, { useEffect, useState, useCallback } from 'react';
import { BACKEND_URL } from '../config/canonical';
import { NxPanel, NxButton } from '../components/nx';
import { Plus, Trash2, Save, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Dashboard 3.0 — Quick Notes.
 *
 * Per-user lightweight scratchpad. Stored server-side so they survive across
 * devices. Newest first. Free-form content (no per-note title required; we
 * auto-derive a title from the first ~48 chars).
 */
export default function QuickNotesPage() {
  const [notes, setNotes] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const token = () => localStorage.getItem('nexus_token');

  const fetchNotes = useCallback(async () => {
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-notes`,
        { headers: { Authorization: `Bearer ${token()}` } });
      const data = await res.json();
      setNotes(data.notes || []);
    } catch (e) { toast.error(`Failed: ${e.message}`); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const startNew = () => { setEditingId('new'); setDraft(''); };

  const startEdit = (n) => { setEditingId(n.id); setDraft(n.content); };

  const save = async () => {
    if (!draft.trim()) { toast.error('Note cannot be empty'); return; }
    try {
      const url = editingId === 'new'
        ? `${BACKEND_URL}/api/quick-notes`
        : `${BACKEND_URL}/api/quick-notes/${editingId}`;
      const method = editingId === 'new' ? 'POST' : 'PATCH';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ content: draft }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(editingId === 'new' ? 'Note saved' : 'Note updated');
      setEditingId(null);
      setDraft('');
      fetchNotes();
    } catch (e) { toast.error(`Save failed: ${e.message}`); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/quick-notes/${id}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      fetchNotes();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  return (
    <div className="nx" data-testid="quick-notes-page">
      <NxPanel
        title="Quick Notes"
        subtitle={`${notes.length} NOTE${notes.length === 1 ? '' : 'S'}`}
        action={
          <NxButton variant="primary" className="nx-btn--sm" onClick={startNew} data-testid="qn-new">
            <Plus size={14} /> New
          </NxButton>
        }
      >
        {editingId && (
          <div className="nx-panel" style={{ padding: 12, marginBottom: 12, background: 'rgba(244,192,74,0.04)', borderColor: 'rgba(244,192,74,0.20)' }}>
            <p className="nx-eyebrow mb-2">{editingId === 'new' ? 'New note' : 'Editing note'}</p>
            <textarea
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              data-testid="qn-editor"
              rows={6}
              placeholder="Jot something down…"
              style={{ width: '100%', padding: 8, background: '#0E1626', border: '1px solid var(--nx-border-soft)', borderRadius: 6, color: '#E2E8F0', fontSize: 13, resize: 'vertical' }}
            />
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <NxButton variant="primary" className="nx-btn--sm" onClick={save} data-testid="qn-save"><Save size={13} /> Save</NxButton>
              <NxButton variant="ghost" className="nx-btn--sm" onClick={() => { setEditingId(null); setDraft(''); }}>Cancel</NxButton>
            </div>
          </div>
        )}

        {busy && notes.length === 0 ? (
          <p style={{ color: '#94A3B8', fontSize: 13 }}>Loading…</p>
        ) : notes.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#94A3B8' }} data-testid="qn-empty">
            <StickyNote size={32} style={{ opacity: 0.4, margin: '0 auto 8px' }} />
            <p style={{ fontSize: 13 }}>No quick notes yet. Tap <strong>New</strong> to add your first.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }} data-testid="qn-list">
            {notes.map(n => (
              <article key={n.id} onClick={() => startEdit(n)} data-testid={`qn-card-${n.id}`}
                className="nx-panel"
                style={{ padding: 12, background: 'rgba(11,20,38,0.5)', cursor: 'pointer' }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#F8FAFC', margin: 0 }}>{n.title}</p>
                <p style={{ fontSize: 12, color: '#CBD5E1', marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {n.content.length > 200 ? n.content.slice(0, 200) + '…' : n.content}
                </p>
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10, color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace' }}>
                  <span>{new Date(n.updated_at).toLocaleString()}</span>
                  <NxButton variant="danger" className="nx-btn--sm" onClick={e => { e.stopPropagation(); remove(n.id); }} data-testid={`qn-del-${n.id}`}>
                    <Trash2 size={12} />
                  </NxButton>
                </div>
              </article>
            ))}
          </div>
        )}
      </NxPanel>
    </div>
  );
}
